'use client'

import { useState } from 'react'
import { Zap, Bot, Network, TrendingUp } from 'lucide-react'
import { IconButton } from '@/components/platform/ui'
import { InstrumentLabel, Panel } from '@/components/radar/ui'
import { X } from 'lucide-react'

interface ToolScores {
  crawl?: number
  citation?: number
  readiness?: number
  robots?: number
  llms?: number
  reddit?: number
}

interface SmartCTAProps {
  scores: ToolScores
  domain: string | null
  unifiedScore: number
}

interface CTAConfig {
  id: string
  icon: typeof Bot
  iconColor: string
  /** Mono eyebrow above the title, e.g. "Hive · AI co-workers". */
  eyebrow: string
  title: string
  message: string
  cta: string
  link: string
  product: 'vector' | 'hive' | 'geo' | 'general'
}

/**
 * Generates contextual CTAs based on audit results.
 * Uses Pixelmojo's consultative approach: show insight, create curiosity, direct to action.
 */
function generateCTAs(scores: ToolScores, unifiedScore: number): CTAConfig[] {
  const ctas: CTAConfig[] = []

  // Low overall score: GEO strategy session
  if (unifiedScore > 0 && unifiedScore < 50) {
    ctas.push({
      id: 'geo-strategy',
      icon: TrendingUp,
      iconColor: '#008dcc',
      eyebrow: 'AI visibility strategy',
      title: 'Your AI visibility needs a strategy overhaul',
      message: `Scoring ${unifiedScore}/100 means AI search engines are largely ignoring your content. Our GEO methodology has helped brands go from invisible to cited in 60 days.`,
      cta: 'Book a GEO Strategy Session',
      link: 'https://www.pixelmojo.io/services/ai-visibility-strategy',
      product: 'geo',
    })
  }

  // Low citation + decent crawl: content authority problem
  if ((scores.citation ?? 100) < 30 && (scores.crawl ?? 0) >= 60) {
    ctas.push({
      id: 'citation-authority',
      icon: Zap,
      iconColor: '#F90B8A',
      eyebrow: 'AI visibility strategy',
      title: 'Technically visible but not being cited',
      message:
        'AI bots can access your site, but models are not mentioning you in responses. This is a content authority and GEO optimization problem, not a technical one. We have a proven playbook for this.',
      cta: 'See How We Fix Citations',
      link: 'https://www.pixelmojo.io/services/ai-visibility-strategy',
      product: 'geo',
    })
  }

  // Multiple tools + low scores: Hive multi-agent positioning
  const toolsRun = Object.values(scores).filter(s => s !== undefined).length
  if (toolsRun >= 4 && unifiedScore < 60) {
    ctas.push({
      id: 'hive-agents',
      icon: Network,
      iconColor: '#008dcc',
      eyebrow: 'Hive · AI co-workers',
      title: 'Automate your AI visibility monitoring',
      message: `You just ran ${toolsRun} tools manually. Hive runs them on schedule, tracks changes, and alerts you the moment a score drops.`,
      cta: 'Explore Hive',
      link: 'https://www.pixelmojo.io/hive',
      product: 'hive',
    })
  }

  // Good scores but no automation: Vector for lead capture
  if (unifiedScore >= 70 && (scores.citation ?? 0) >= 50) {
    ctas.push({
      id: 'vector-leads',
      icon: Bot,
      iconColor: '#22c55e',
      eyebrow: 'Vector · AI sales agent',
      title: 'You are visible to AI. Now capture the leads.',
      message:
        'Your AI visibility is strong, which means AI-referred traffic is coming. Vector qualifies these visitors in real-time using 12-dimension scoring, so your sales team only talks to ready buyers.',
      cta: 'See Vector in Action',
      link: 'https://www.pixelmojo.io/vector',
      product: 'vector',
    })
  }

  return ctas.slice(0, 2) // Max 2 CTAs
}

/**
 * Generates a prospect readiness score based on audit behavior.
 * Higher score = more likely to be a good fit for Pixelmojo services.
 */
export function calculateProspectScore(
  scores: ToolScores,
  unifiedScore: number
): {
  score: number
  level: 'cold' | 'warm' | 'hot'
  signals: string[]
} {
  let score = 0
  const signals: string[] = []

  // Ran multiple tools = serious about AI visibility
  const toolsRun = Object.values(scores).filter(s => s !== undefined).length
  if (toolsRun >= 4) {
    score += 20
    signals.push(`Ran ${toolsRun} tools (comprehensive audit)`)
  } else if (toolsRun >= 2) {
    score += 10
    signals.push(`Ran ${toolsRun} tools`)
  }

  // Low scores = has pain, needs help
  if (unifiedScore > 0 && unifiedScore < 40) {
    score += 25
    signals.push('Low AI visibility score (high need)')
  } else if (unifiedScore >= 40 && unifiedScore < 70) {
    score += 15
    signals.push('Moderate score (room for improvement)')
  }

  // Specific problem areas
  if ((scores.citation ?? 100) < 30) {
    score += 15
    signals.push('Poor citation rate (content authority gap)')
  }
  if ((scores.crawl ?? 100) < 50) {
    score += 10
    signals.push('Crawlability issues (technical blockers)')
  }
  if ((scores.llms ?? 100) < 30) {
    score += 10
    signals.push('Missing or weak llms.txt')
  }

  // Running citation check = cares about brand perception in AI
  if (scores.citation !== undefined) {
    score += 10
    signals.push('Checked AI citations (brand-aware)')
  }

  // Running reddit = cares about community signal
  if (scores.reddit !== undefined) {
    score += 5
    signals.push('Monitoring Reddit presence')
  }

  const level: 'cold' | 'warm' | 'hot' =
    score >= 60 ? 'hot' : score >= 30 ? 'warm' : 'cold'

  return { score: Math.min(score, 100), level, signals }
}

export default function SmartCTA({
  scores,
  domain: _domain,
  unifiedScore,
}: SmartCTAProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const ctas = generateCTAs(scores, unifiedScore).filter(
    c => !dismissed.has(c.id)
  )

  if (ctas.length === 0) return null

  return (
    <div className='flex flex-col gap-3'>
      {ctas.map(cta => {
        const Icon = cta.icon
        return (
          <Panel key={cta.id} gradientRule className='relative'>
            <div className='absolute right-2 top-3 z-10'>
              <IconButton
                icon={X}
                aria-label={`Dismiss: ${cta.title}`}
                variant='ghost'
                size='sm'
                onClick={() => setDismissed(prev => new Set([...prev, cta.id]))}
              />
            </div>

            <div className='flex flex-col gap-5 px-6 py-7 sm:flex-row sm:items-center sm:gap-7 sm:px-8'>
              {/* Outlined tile, not a filled chip: the reference expresses the
                  product accent as a hairline so the card reads as one flat
                  surface rather than a stack of tinted boxes. */}
              <span
                aria-hidden='true'
                className='flex h-14 w-14 shrink-0 items-center justify-center border'
                style={{
                  borderColor: `color-mix(in srgb, ${cta.iconColor} 40%, transparent)`,
                }}
              >
                <Icon size={24} style={{ color: cta.iconColor }} />
              </span>

              <div className='flex min-w-0 flex-1 flex-col gap-2'>
                <InstrumentLabel
                  tone='inherit'
                  className='pr-8'
                  style={{ color: cta.iconColor }}
                >
                  {cta.eyebrow}
                </InstrumentLabel>
                <span className='font-display text-xl font-semibold tracking-[-0.02em]'>
                  {cta.title}
                </span>
                <span className='max-w-[68ch] text-pretty text-sm leading-relaxed text-muted-foreground'>
                  {cta.message}
                </span>
              </div>

              <a
                href={cta.link}
                data-cta-id={`smart_${cta.id.replace(/-/g, '_')}`}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex h-11 shrink-0 items-center justify-center bg-[var(--cta-fill)] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#c3086e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
              >
                {cta.cta}
              </a>
            </div>
          </Panel>
        )
      })}
    </div>
  )
}
